import { forwardRef, useCallback, useEffect, useImperativeHandle, useLayoutEffect, useMemo, useRef, useState } from "react";
import { Message, MessageAttachment } from "../../types/Agent";
import AgentInput, { PendingFile, UploadedFile } from "./AgentInput";
import AgentMessage from "./AgentMessage";
import { ReactComponent as ErrorIconSVG } from '../../assets/images/error-icon.svg';
import { ReactComponent as DownArrowSVG } from '../../assets/images/down-arrow.svg';
import Button from "../Button";
import { BACK4APP_DOT_COM_SITE_URL } from "../../settings";
import useIsMobile from "../../hooks/useIsMobile";
import LoadingSpinner from "../LoadingSpinner";

const Agent = forwardRef<
  { setInputContent: (content: string) => void; scrollToBottom: () => void },
  {
    agentId: string;
    chatId: string;
    messages?: Message[];
    isSidebarOpen: boolean;
    paymentErrorMessage?: string;
    onUpgrade?: () => void;
    isBusy?: boolean;
    onSend: (content: string, uploadedFiles?: UploadedFile[]) => Promise<boolean>;
    onStop?: () => Promise<boolean>;
    onAttachmentClick?: (attachment: MessageAttachment) => void;
    inputContent?: string;
    onInputContentChange?: (value: string) => void;
    pendingFiles?: PendingFile[];
    onPendingFilesChange?: (
      files: PendingFile[] | ((prev: PendingFile[]) => PendingFile[])
    ) => void;
    showAttachments?: boolean;
    scrollToBottomOffsetRem?: number;

    // lazy load chat messages
    hasMoreOlder?: boolean;
    isLoadingOlder?: boolean;
    onLoadOlder?: () => void;
  }
>(({ 
  agentId, 
  chatId, 
  messages, 
  onSend, 
  isSidebarOpen, 
  paymentErrorMessage, 
  onUpgrade, 
  isBusy, 
  onStop, 
  onAttachmentClick, 
  inputContent, 
  onInputContentChange, 
  pendingFiles, 
  onPendingFilesChange, 
  showAttachments, 
  scrollToBottomOffsetRem,

  // lazy load chat messages
  hasMoreOlder,
  isLoadingOlder,
  onLoadOlder,
}, ref) => {
  const handleSend = async (content: string, uploadedFiles: UploadedFile[]) => {
    if (isBusy) return false;
    return onSend(content, uploadedFiles);
  };
  const [autoScroll, setAutoScroll] = useState(true);
  const [showScrollToBottom, setShowScrollToBottom] = useState(false);
  const isMobile = useIsMobile();
  const [scrollButtonOffsetRem, setScrollButtonOffsetRem] = useState(8); // Tracks banner visibility changes (desktop only)
  const boxRef = useRef<HTMLDivElement>(null);
  const agentInputRef = useRef<{ setContent: (content: string) => void }>(null);
  const bottomThresholdPx = useMemo(() => {
    // The messages container uses `pb-[12rem]` to reserve space for the input area.
    // Without accounting for that padding, the user can "visually" be at the bottom
    // while the scroll position isn't at the true scrollHeight, disabling auto-scroll.
    if (typeof window === 'undefined') return 8;
    const remPx = Number.parseFloat(window.getComputedStyle(document.documentElement).fontSize || '16') || 16;
    const bottomPaddingRem = 12;
    return Math.max(8, bottomPaddingRem * remPx);
  }, []);

  //preserve scroll position when we prepend older messages
  const pendingPrependRef = useRef(false);
  const prevScrollHeightRef = useRef(0);
  const prevScrollTopRef = useRef(0);
  const prevMessagesLenOnLoadOlderRef = useRef(0);
  const wasLoadingOlderRef = useRef(false);

  const maybeLoadOlder = useCallback(() => {
    const el = boxRef.current;
    if (!el) return;
    if (!onLoadOlder) return;
    if (!hasMoreOlder) return;
    if (isLoadingOlder) return;

    // near top => load older
    if (el.scrollTop <= 200) {
      // capture once per attempt
      if (!pendingPrependRef.current) {
        prevScrollHeightRef.current = el.scrollHeight;
        prevScrollTopRef.current = el.scrollTop;
        prevMessagesLenOnLoadOlderRef.current = messages?.length ?? 0;
        pendingPrependRef.current = true;
      }
  
      onLoadOlder();
    }
  }, [onLoadOlder, hasMoreOlder, isLoadingOlder, messages?.length]);

  // if loading finished but messages didn't change, clear pending anchor
  useEffect(() => {
    if (!wasLoadingOlderRef.current && isLoadingOlder) {
      wasLoadingOlderRef.current = true;
      return;
    }

    if (wasLoadingOlderRef.current && !isLoadingOlder) {
      wasLoadingOlderRef.current = false;

      const curLen = messages?.length ?? 0;
      if (pendingPrependRef.current && curLen === prevMessagesLenOnLoadOlderRef.current) {
        pendingPrependRef.current = false;
      }
    }
  }, [isLoadingOlder, messages?.length]);
  
  // Calculate base offset: mobile = 12rem, desktop = 8rem
  const baseOffsetRem = isMobile ? 12 : 8;
  
  // Calculate effective offset: 
  // - If prop is provided (mobile), use it directly
  // - Otherwise (desktop), use state which tracks banner visibility changes
  const effectiveScrollButtonOffsetRem = scrollToBottomOffsetRem !== undefined 
    ? scrollToBottomOffsetRem 
    : scrollButtonOffsetRem;

  // Update state to match base when mobile state changes (desktop only, when not showing banner)
  useEffect(() => {
    if (scrollToBottomOffsetRem === undefined && scrollButtonOffsetRem !== 10.5) {
      setScrollButtonOffsetRem(baseOffsetRem);
    }
  }, [isMobile, scrollToBottomOffsetRem, baseOffsetRem]);

  useImperativeHandle(ref, () => ({
    setInputContent: (content: string) => {
      if (agentInputRef.current) {
        agentInputRef.current.setContent(content);
      }
    },
    scrollToBottom: () => {
      const element = boxRef.current;
      if (!element) return;
      // Force jump to bottom (used after sending a message, even if user scrolled up)
      element.scrollTop = element.scrollHeight;
      setAutoScroll(true);
      setShowScrollToBottom(false);
    }
  }));

  const updateScrollState = useCallback(() => {
    const element = boxRef.current;
    if (!element) return;

    const isScrollable = element.scrollHeight - element.clientHeight > 8;
    const isAtBottom = element.scrollTop + element.clientHeight >= element.scrollHeight - bottomThresholdPx;

    setAutoScroll(isAtBottom);
    setShowScrollToBottom(isScrollable && !isAtBottom);

    maybeLoadOlder();
  }, [bottomThresholdPx, maybeLoadOlder]);

  useLayoutEffect(() => {
    const el = boxRef.current;
    if (!el) return;
  
    // If we triggered "load older", keep viewport anchored after prepend
    if (pendingPrependRef.current) {
      const delta = el.scrollHeight - prevScrollHeightRef.current;
      el.scrollTop = prevScrollTopRef.current + delta;
      pendingPrependRef.current = false;
    }
  }, [messages]);

  useEffect(() => {
    const el = boxRef.current;
    if (!el) return;
  
    // autoscroll to bottom only when user is at bottom
    if (messages && autoScroll) {
      el.scrollTop = el.scrollHeight;
    }
  
    requestAnimationFrame(updateScrollState);
  }, [messages, autoScroll, updateScrollState]);

  const handleScrollToBottom = () => {
    const element = boxRef.current;
    if (!element) return;
    element.scrollTo({ top: element.scrollHeight, behavior: 'smooth' });
    setAutoScroll(true);
    setShowScrollToBottom(false);
  };

  const handleCreditsBannerVisibilityChange = useCallback((isVisible: boolean) => {
    if (scrollToBottomOffsetRem !== undefined) return;
    const baseOffset = isMobile ? 12 : 8;
    setScrollButtonOffsetRem(isVisible ? 10.5 : baseOffset);
  }, [scrollToBottomOffsetRem, isMobile]);

  return (
    <div className="relative h-full w-full">
      <div className="h-full w-full overflow-y-scroll glass-scroll" ref={boxRef} onScroll={updateScrollState}>
        {/* Maximize horizontal space for messages (no side paddings) */}
        <div className="w-full px-0 pb-[12rem] py-4">
          {isLoadingOlder && (
            <div className="w-full flex justify-center py-3">
              <LoadingSpinner width="1rem" height="1rem" />
            </div>
          )}
          <AgentMessage
            message={{
              id: 'welcome',
              role: 'assistant',
              content:  `Hi, I'm your Web App Builder.
              Tell me what you want to build and I’ll create a working app for you.`
            }}
          />
          {messages?.map(message => 
            <AgentMessage 
              key={message.id} 
              message={message} 
              onAttachmentClick={onAttachmentClick} 
            />
          )}
          {paymentErrorMessage && (
            <div className="grow flex flex-col items-center justify-center mt-10">
            <ErrorIconSVG />
            <div className="mt-4 font-sora font-semibold text-lg leading-[140%] text-light-grey mb-8">
              {paymentErrorMessage}
            </div>
            <Button
              type='submit'
              onClick={() => {
                if (onUpgrade) {
                  onUpgrade();
                  return;
                }
                window.open(`${BACK4APP_DOT_COM_SITE_URL}/pricing/agent?appId=${agentId}&type=agent`, '_blank', 'noopener,noreferrer');
              }}
            >
              Pay Now
            </Button>
          </div>
          )}
        </div>
      </div>
      {showScrollToBottom && (
        <button
          type="button"
          onClick={handleScrollToBottom}
          className={`absolute right-5 z-20 rounded-md p-2 !bg-[#222832] text-white/80 hover:text-white hover:border-white/20 shadow-lg transition ${
            effectiveScrollButtonOffsetRem === 10.5 
              ? 'bottom-[12rem] lg:bottom-[10.5rem]'
              : 'bottom-[9.5rem] lg:bottom-[8rem]'
          }`}
          aria-label="Scroll to bottom"
          title="Scroll to bottom"
        >
          <DownArrowSVG className="mx-auto h-3 w-3" />
        </button>
      )}
      {/* Hidden on mobile - mobile has its own input in MyAgents.tsx */}
      <div className="hidden lg:block">
        <AgentInput
          chatId={chatId}
          onSend={handleSend}
          onStop={onStop}
          onUpgrade={onUpgrade}
          onCreditsBannerVisibilityChange={handleCreditsBannerVisibilityChange}
          ref={agentInputRef}
          disabled={isBusy}
          isResponding={isBusy}
          value={inputContent}
          onValueChange={onInputContentChange}
          pendingFiles={pendingFiles}
          onPendingFilesChange={onPendingFilesChange}
          showAttachments={showAttachments}
        />
      </div>
    </div>
  )
});

export default Agent;
